/**
 * Customer Success — Onglet Engagement client (refonte mockup 22/05/2026).
 *
 * Layout :
 * - Header : recherche société + "Analyser le client" + sélecteur période + Rafraîchir + Exporter
 * - 6 KPI cards : Clients actifs, Clients à risque, Utilisateurs actifs,
 *                 Fonctionnalités inutilisées, Dernière activité, Taux d'adoption (donut)
 * - 2 colonnes : Clients à suivre (tableau enrichi) + Actions recommandées (4 cartes)
 * - 3 colonnes : Adoption produit (tableau avec filtre catégorie) + Pages les + consultées + Activité récente
 *
 * Données via GET /api/admin/client-engagement/dashboard (endpoint agrégé).
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Activity, Users, AlertTriangle, Package, Clock, TrendingUp,
  RefreshCw, Download, Search, Eye, MessageCircle,
  Phone, GraduationCap, FileText, ArrowRight, UserCheck, Layout, FileSearch,
} from 'lucide-react';
import {
  ClientActionsProvider, useClientActions,
} from './actions/ClientActionsContext';
import ClientActionsHub from './actions/ClientActionsHub';
import ClientActionsMenu from './actions/ClientActionsMenu';
import AllRecommendedActionsModal from './actions/AllRecommendedActionsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_CATEGORIES = [
  { id: 'all', label: 'Toutes' },
  { id: 'operations', label: 'Opérations' },
  { id: 'finance', label: 'Finance' },
  { id: 'commercial', label: 'Commercial' },
  { id: 'support', label: 'Support' },
];

const RISK_BADGE = {
  faible: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  a_surveiller: 'bg-amber-50 text-amber-700 border-amber-200',
  a_relancer: 'bg-red-50 text-red-700 border-red-200',
};
const RISK_LABEL = { faible: 'Faible', a_surveiller: 'À surveiller', a_relancer: 'À relancer' };

const PRIORITY_BADGE = {
  haute: 'bg-red-50 text-red-700 border-red-200',
  moyenne: 'bg-amber-50 text-amber-700 border-amber-200',
  basse: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const STATUS_BADGE = {
  critique: 'bg-red-50 text-red-700 border-red-200',
  a_surveiller: 'bg-amber-50 text-amber-700 border-amber-200',
  bon: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};
const STATUS_LABEL = { critique: 'Critique', a_surveiller: 'À surveiller', bon: 'Bon' };

const CATEGORY_BADGE = {
  operations: 'bg-blue-50 text-blue-700 border-blue-200',
  finance: 'bg-violet-50 text-violet-700 border-violet-200',
  commercial: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  support: 'bg-slate-100 text-slate-700 border-slate-200',
};
const CATEGORY_LABEL = { operations: 'Opérations', finance: 'Finance', commercial: 'Commercial', support: 'Support' };

const EVENT_LABEL = {
  'session.started': 'Connexion utilisateur',
  'page.viewed': 'Consultation page',
  'page.duration': 'Temps passé',
  'feature.used': 'Action métier',
  'search.performed': 'Recherche',
  'filter.applied': 'Filtre',
  'dashboard.opened': 'Ouverture dashboard',
  'tracking.viewed': 'Consultation suivi',
  'document.downloaded': 'Téléchargement',
};

const EVENT_ICON = {
  'session.started': UserCheck,
  'page.viewed': Eye,
  'feature.used': Activity,
  'search.performed': Search,
  'dashboard.opened': Layout,
  'tracking.viewed': Clock,
  'document.downloaded': Download,
  'page.duration': Clock,
};

const EVENT_ICON_BG = {
  'session.started': 'bg-emerald-100 text-emerald-700',
  'page.viewed': 'bg-blue-100 text-blue-700',
  'feature.used': 'bg-amber-100 text-amber-700',
  'search.performed': 'bg-indigo-100 text-indigo-700',
  'dashboard.opened': 'bg-violet-100 text-violet-700',
  'tracking.viewed': 'bg-cyan-100 text-cyan-700',
  'document.downloaded': 'bg-orange-100 text-orange-700',
  'page.duration': 'bg-slate-100 text-slate-700',
};

const fmtDate = (iso, withTime = true) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const today = new Date();
    const same = d.toDateString() === today.toDateString();
    if (same && withTime) return `Aujourd'hui ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    if (same) return "Aujourd'hui";
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) +
      (withTime ? ` ${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}` : '');
  } catch { return iso; }
};
const fmtRelative = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Math.floor((Date.now() - d.getTime()) / 1000);
  if (diff < 60) return `il y a ${diff}s`;
  if (diff < 3600) return `il y a ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (diff < 172800) return 'Hier';
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
};
const fmtNumber = (n) => (n === null || n === undefined ? '—' : new Intl.NumberFormat('fr-FR').format(n));

// Avatar coloré déterministe à partir des initiales société
const COLORS = ['bg-emerald-500', 'bg-blue-500', 'bg-violet-500', 'bg-amber-500', 'bg-rose-500', 'bg-indigo-500', 'bg-teal-500'];
const initialsBg = (name = '') => {
  const h = name.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  return COLORS[h % COLORS.length];
};
const initials = (name = '') => (name.split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('') || 'CL').toUpperCase();

// Donut SVG simple (pourcentage)
function Donut({ value = 0, color = 'emerald', size = 56, stroke = 6 }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c * (1 - Math.max(0, Math.min(100, value)) / 100);
  const colorMap = {
    emerald: '#10b981', blue: '#3b82f6', amber: '#f59e0b', red: '#ef4444', violet: '#8b5cf6',
  };
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="block">
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e5e7eb" strokeWidth={stroke} />
      <circle
        cx={size / 2} cy={size / 2} r={r} fill="none"
        stroke={colorMap[color] || colorMap.emerald} strokeWidth={stroke}
        strokeDasharray={c} strokeDashoffset={offset} strokeLinecap="round"
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      <text x="50%" y="50%" textAnchor="middle" dy="0.35em" className="fill-gray-900" style={{ fontSize: size * 0.28, fontWeight: 600 }}>
        {value}
      </text>
    </svg>
  );
}

// Barre de progression légère
function Progress({ value = 0, total = 1, color = 'emerald' }) {
  const pct = Math.round((value / Math.max(1, total)) * 100);
  const colorMap = {
    emerald: 'bg-emerald-500', amber: 'bg-amber-500', red: 'bg-red-500',
  };
  return (
    <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
      <div className={`h-full ${colorMap[color] || colorMap.emerald}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

// Icône de priorité pour Actions recommandées
const ACTION_ICONS = {
  recall_inactive: { icon: Phone, bg: 'bg-red-100 text-red-700' },
  train_features: { icon: GraduationCap, bg: 'bg-amber-100 text-amber-700' },
  promote_tracking: { icon: Clock, bg: 'bg-blue-100 text-blue-700' },
  encourage_docs: { icon: FileText, bg: 'bg-emerald-100 text-emerald-700' },
};

// Dots d'impact métier (1-3)
function ImpactDots({ value = 0 }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3].map((i) => (
        <span key={i} className={`block w-1.5 h-1.5 rounded-full ${i <= value ? 'bg-red-500' : 'bg-gray-200'}`} />
      ))}
      <span className="text-[10px] text-gray-500 ml-1">
        {value === 3 ? 'Très important' : value === 2 ? 'Important' : 'Moyen'}
      </span>
    </div>
  );
}

export default function ClientEngagementTab() {
  return (
    <ClientActionsProvider>
      <ClientEngagementTabInner />
      <ClientActionsHub />
    </ClientActionsProvider>
  );
}

function ClientEngagementTabInner() {
  const { open } = useClientActions();
  const [days, setDays] = useState(30);
  const [showAllRecommended, setShowAllRecommended] = useState(false);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Modals - SUPPRIMÉ "Tous les clients" → expansion in-place
  // Affichage incrémental in-place pour la liste des clients
  const [clientsShowCount, setClientsShowCount] = useState(10);
  const [allClients, setAllClients] = useState([]);
  const [allClientsLoading, setAllClientsLoading] = useState(false);

  const [showAllFeatures, setShowAllFeatures] = useState(false);

  // Debounce recherche
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (debouncedSearch) params.set('search', debouncedSearch);
      const r = await axios.get(`${API}/admin/client-engagement/dashboard?${params}`);
      setData(r.data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Engagement dashboard error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [days, debouncedSearch]);

  // Charger TOUS les clients (in-place, pas de modal)
  const fetchAllClients = async () => {
    setAllClientsLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days), limit: '300' });
      // Reuse top-level search bar (header) pour filtrer la liste in-place aussi
      if (debouncedSearch) params.set('search', debouncedSearch);
      const r = await axios.get(`${API}/admin/client-engagement/clients-list?${params}`);
      setAllClients(r.data?.clients || []);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Clients fetch error', e);
      setAllClients([]);
    } finally {
      setAllClientsLoading(false);
    }
  };

  // Charge la liste enrichie au montage + à chaque changement de période/recherche
  useEffect(() => {
    fetchAllClients();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [days, debouncedSearch]);

  const exportCSV = () => {
    if (!data?.clientsToWatch?.length) return;
    const lines = [
      ['Société', 'Score', 'Risque', 'Utilisateurs', 'Features utilisées', 'Dernière activité'],
      ...data.clientsToWatch.map(c => [
        c.companyName, c.score, RISK_LABEL[c.risk] || c.risk,
        c.uniqueUsers, `${c.featuresUsedCount}/${c.totalCriticalFeatures}`,
        fmtDate(c.lastSeen, false),
      ]),
    ];
    const csv = lines.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `engagement-${days}j-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const filteredAdoption = useMemo(() => {
    if (!data?.adoptionByFeature) return [];
    if (activeCategory === 'all') return data.adoptionByFeature;
    return data.adoptionByFeature.filter(f => f.category === activeCategory);
  }, [data, activeCategory]);

  const kpis = data?.kpis || {};
  const adoptionColor = (kpis.adoptionRatePct || 0) >= 70 ? 'emerald' : (kpis.adoptionRatePct || 0) >= 40 ? 'amber' : 'red';
  const adoptionLabel = (kpis.adoptionRatePct || 0) >= 70 ? 'Bon' : (kpis.adoptionRatePct || 0) >= 40 ? 'Moyen' : 'Faible';

  return (
    <div className="space-y-6" data-testid="engagement-tab">
      {/* ════════ Header : recherche + actions ════════ */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher un client..."
              className="pl-9 w-72"
              data-testid="company-search-input"
            />
          </div>
          <Button variant="default" size="sm" className="bg-gray-900 hover:bg-gray-800 text-white" data-testid="analyze-client-btn">
            <Activity className="w-4 h-4 mr-2" /> Analyser le client
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-40" data-testid="engagement-period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Aujourd'hui</SelectItem>
              <SelectItem value="7">7 derniers jours</SelectItem>
              <SelectItem value="30">30 derniers jours</SelectItem>
              <SelectItem value="90">90 derniers jours</SelectItem>
              <SelectItem value="180">180 derniers jours</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="engagement-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Rafraîchir
          </Button>
          <Button variant="outline" size="sm" onClick={exportCSV} data-testid="engagement-export-btn">
            <Download className="w-4 h-4 mr-2" /> Exporter
          </Button>
        </div>
      </div>

      {/* ════════ 6 KPI cards ════════ */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <KpiCard
          icon={Users} iconBg="bg-emerald-100 text-emerald-700"
          label="Clients actifs" value={fmtNumber(kpis.activeClients)}
          delta={kpis.activeClientsDelta} testid="kpi-active-clients"
        />
        <KpiCard
          icon={AlertTriangle} iconBg="bg-amber-100 text-amber-700"
          label="Clients à risque" value={fmtNumber(kpis.atRiskClients)}
          delta={null} subtitle={kpis.atRiskClients > 0 ? 'à surveiller' : 'tout va bien'}
          testid="kpi-at-risk"
        />
        <KpiCard
          icon={UserCheck} iconBg="bg-violet-100 text-violet-700"
          label="Utilisateurs actifs" value={fmtNumber(kpis.activeUsers)}
          delta={kpis.activeUsersDelta} testid="kpi-active-users"
        />
        <KpiCard
          icon={Package} iconBg="bg-indigo-100 text-indigo-700"
          label="Fonctionnalités inutilisées" value={fmtNumber(kpis.unusedFeatures)}
          delta={0} testid="kpi-unused"
        />
        <KpiCard
          icon={Clock} iconBg="bg-slate-100 text-slate-700"
          label="Dernière activité" value={fmtDate(kpis.lastActivity).split(' ')[0] || '—'}
          subtitle={kpis.lastActivity ? new Date(kpis.lastActivity).toLocaleDateString('fr-FR') : ''}
          testid="kpi-last-activity"
        />
        <Card data-testid="kpi-adoption" className="overflow-hidden">
          <CardContent className="pt-5 pb-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[11px] text-gray-500 leading-tight">Taux d'adoption produit</p>
                <p className="text-xl font-semibold text-gray-900 mt-1">{kpis.adoptionRatePct ?? 0}%</p>
                <p className={`text-[11px] mt-1 font-medium ${adoptionColor === 'emerald' ? 'text-emerald-600' : adoptionColor === 'amber' ? 'text-amber-600' : 'text-red-600'}`}>
                  {adoptionLabel}
                </p>
              </div>
              <Donut value={kpis.adoptionRatePct ?? 0} color={adoptionColor} size={56} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ════════ 2 colonnes : Clients à suivre + Actions recommandées ════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Clients à suivre — liste in-place paginée */}
        <Card className="lg:col-span-2" data-testid="clients-to-watch-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Users className="w-4 h-4 text-blue-500" />
                Clients à suivre
                <span className="text-xs font-normal text-gray-400">
                  ({Math.min(clientsShowCount, allClients.length)} / {allClients.length})
                </span>
              </CardTitle>
              <div className="flex items-center gap-2">
                {clientsShowCount < allClients.length && (
                  <button
                    onClick={() => setClientsShowCount(allClients.length)}
                    className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    data-testid="see-all-clients-btn"
                  >
                    Tout afficher ({allClients.length}) <ArrowRight className="w-3 h-3" />
                  </button>
                )}
                {clientsShowCount > 10 && (
                  <button
                    onClick={() => setClientsShowCount(10)}
                    className="text-xs text-gray-500 hover:text-gray-700"
                    data-testid="collapse-clients-btn"
                  >
                    Réduire
                  </button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {allClientsLoading && allClients.length === 0 ? (
              <p className="text-sm text-gray-500 py-8 text-center">Chargement...</p>
            ) : !allClients.length ? (
              <p className="text-sm text-gray-500">Aucun client à afficher.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Client</TableHead>
                    <TableHead className="text-xs">Score</TableHead>
                    <TableHead className="text-xs">Dernière activité</TableHead>
                    <TableHead className="text-xs text-center">Users</TableHead>
                    <TableHead className="text-xs">Features</TableHead>
                    <TableHead className="text-xs">Risque</TableHead>
                    <TableHead className="text-xs text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allClients.slice(0, clientsShowCount).map((c) => {
                    const scoreColor = c.score >= 70 ? 'emerald' : c.score >= 40 ? 'amber' : 'red';
                    return (
                      <TableRow
                        key={c.companyName}
                        data-testid={`client-row-${c.companyName}`}
                        onClick={() => open('view', c)}
                        className="cursor-pointer hover:bg-gray-50/60"
                      >
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <div className={`w-9 h-9 rounded-full ${initialsBg(c.companyName)} text-white flex items-center justify-center text-xs font-semibold`}>
                              {initials(c.companyName)}
                            </div>
                            <div className="min-w-0">
                              <p className="font-medium text-sm text-gray-900 truncate max-w-[200px]">{c.companyName}</p>
                              <p className="text-[11px] text-gray-500 truncate max-w-[200px]">{c.primaryEmail || `${c.knownUsers || 0} user(s)`}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Donut value={c.score} color={scoreColor} size={40} stroke={4} />
                            <span className="text-[11px] text-gray-400">/100</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-xs text-gray-900">{c.lastSeen ? fmtDate(c.lastSeen, false) : <span className="text-gray-400">Jamais</span>}</p>
                          <p className="text-[10px] text-gray-500">
                            {c.daysSinceLast === null ? `Aucun event sur ${days}j`
                              : c.daysSinceLast === 0 ? "Aujourd'hui"
                              : `Il y a ${c.daysSinceLast} jour${c.daysSinceLast > 1 ? 's' : ''}`}
                          </p>
                        </TableCell>
                        <TableCell className="text-center text-sm">{c.uniqueUsers || c.knownUsers || 0}</TableCell>
                        <TableCell className="min-w-[100px]">
                          <div className="space-y-1">
                            <p className="text-xs text-gray-700">{c.featuresUsedCount} / {c.totalCriticalFeatures}</p>
                            <Progress value={c.featuresUsedCount} total={c.totalCriticalFeatures} color={scoreColor} />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={`${RISK_BADGE[c.risk]} border text-[10px] px-2 py-0.5`}>
                            {RISK_LABEL[c.risk] || c.risk}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                            <Button
                              variant="ghost" size="icon" className="h-7 w-7"
                              onClick={() => open('view', c)}
                              data-testid={`action-view-${c.companyName}`}
                              aria-label="Voir le client"
                            >
                              <Eye className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="ghost" size="icon" className="h-7 w-7"
                              onClick={() => open('relance', c)}
                              data-testid={`action-relance-${c.companyName}`}
                              aria-label="Relancer le client"
                            >
                              <MessageCircle className="w-3.5 h-3.5" />
                            </Button>
                            <ClientActionsMenu client={c} />
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
            {/* Footer paginé : "Voir plus (+10)" si possible */}
            {clientsShowCount < allClients.length && (
              <div className="mt-3 text-center">
                <Button
                  variant="outline" size="sm"
                  onClick={() => setClientsShowCount((n) => Math.min(n + 10, allClients.length))}
                  data-testid="see-more-clients-btn"
                >
                  Voir plus (+{Math.min(10, allClients.length - clientsShowCount)})
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Actions recommandées */}
        <Card data-testid="recommended-actions-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-emerald-500" />
                Actions recommandées
              </CardTitle>
              <button
                onClick={() => setShowAllRecommended(true)}
                className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
                data-testid="see-all-recommended-btn"
              >
                Voir toutes <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {!data?.recommendedActions?.length ? (
              <p className="text-sm text-gray-500">Aucune action recommandée pour le moment.</p>
            ) : data.recommendedActions.map((a) => {
              const meta = ACTION_ICONS[a.id] || { icon: Activity, bg: 'bg-slate-100 text-slate-700' };
              const Icon = meta.icon;
              // Cible : 1er client "à relancer" sinon 1er de la liste à suivre
              const targetClient =
                allClients.find((cl) => cl.risk === 'a_relancer') ||
                allClients.find((cl) => cl.risk === 'a_surveiller') ||
                allClients[0] || data?.clientsToWatch?.[0];
              return (
                <div
                  key={a.id}
                  className="border border-gray-100 rounded-lg p-3 hover:bg-gray-50 transition-colors cursor-pointer"
                  data-testid={`action-card-${a.id}`}
                  onClick={() => targetClient && open('recommended', targetClient, a)}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-9 h-9 rounded-lg ${meta.bg} flex items-center justify-center shrink-0`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className="text-sm font-medium text-gray-900 leading-tight">{a.title}</h4>
                        <Badge className={`${PRIORITY_BADGE[a.priority] || ''} border text-[10px] px-1.5 py-0.5 capitalize`}>
                          {a.priority}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 leading-snug">{a.description}</p>
                      <Button
                        variant="outline" size="sm" className="mt-2 h-7 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (targetClient) open('recommended', targetClient, a);
                        }}
                        data-testid={`action-cta-${a.id}`}
                      >
                        {a.cta}
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </CardContent>
        </Card>
      </div>

      {/* ════════ 3 colonnes : Adoption produit + Pages + Activité ════════ */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Adoption produit (occupe 1/3, élargi) */}
        <Card className="lg:col-span-2" data-testid="adoption-product-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Package className="w-4 h-4 text-indigo-500" />
              Adoption produit
            </CardTitle>
            {/* Onglets catégories */}
            <div className="flex items-center gap-1 mt-2 flex-wrap">
              {FEATURE_CATEGORIES.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setActiveCategory(cat.id)}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${
                    activeCategory === cat.id
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  data-testid={`category-${cat.id}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs">Fonctionnalité</TableHead>
                  <TableHead className="text-xs">Catégorie</TableHead>
                  <TableHead className="text-xs text-right">Utilisations</TableHead>
                  <TableHead className="text-xs text-right">Sociétés</TableHead>
                  <TableHead className="text-xs">Impact métier</TableHead>
                  <TableHead className="text-xs">Action recommandée</TableHead>
                  <TableHead className="text-xs">Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAdoption.slice(0, 8).map((f) => (
                  <TableRow key={f.feature} data-testid={`adoption-row-${f.feature}`}>
                    <TableCell className="text-sm font-medium">{f.label}</TableCell>
                    <TableCell>
                      <Badge className={`${CATEGORY_BADGE[f.category] || ''} border text-[10px] px-2 py-0.5`}>
                        {CATEGORY_LABEL[f.category] || f.category}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmtNumber(f.uses)}</TableCell>
                    <TableCell className="text-right font-mono text-sm">{fmtNumber(f.companies)}</TableCell>
                    <TableCell><ImpactDots value={f.impact} /></TableCell>
                    <TableCell className="text-xs text-gray-700">{f.action}</TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_BADGE[f.status] || ''} border text-[10px] px-2 py-0.5`}>
                        {STATUS_LABEL[f.status]}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="mt-3 text-center">
              <button onClick={() => setShowAllFeatures(true)} className="text-xs text-blue-600 hover:text-blue-700 inline-flex items-center gap-1" data-testid="see-all-features-btn">
                Voir toutes les fonctionnalités ({data?.adoptionByFeature?.length || 0}) <ArrowRight className="w-3 h-3" />
              </button>
            </div>
          </CardContent>
        </Card>

        {/* Pages les plus consultées + Activité récente (empilées) */}
        <div className="space-y-4">
          <Card data-testid="top-pages-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <FileSearch className="w-4 h-4 text-blue-500" />
                Pages les plus consultées
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!data?.topPages?.length ? (
                <p className="text-xs text-gray-400">Aucune donnée</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-[10px]">Page</TableHead>
                      <TableHead className="text-[10px] text-right">Vues</TableHead>
                      <TableHead className="text-[10px] text-right">Users</TableHead>
                      <TableHead className="text-[10px]">Interprétation</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.topPages.slice(0, 6).map((p) => (
                      <TableRow key={p.path} data-testid="top-page-row">
                        <TableCell><code className="text-[11px]">{p.path}</code></TableCell>
                        <TableCell className="text-right text-xs font-mono">{p.views}</TableCell>
                        <TableCell className="text-right text-xs font-mono">{p.uniqueUsers}</TableCell>
                        <TableCell className={`text-[11px] font-medium ${
                          p.interpretation === 'Forte utilisation' ? 'text-emerald-600' :
                          p.interpretation === 'Moyenne utilisation' ? 'text-blue-600' :
                          p.interpretation === 'Faible utilisation' ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {p.interpretation}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <Card data-testid="recent-activity-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="w-4 h-4 text-emerald-500" />
                Activité récente
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!data?.recentActivity?.length ? (
                <p className="text-xs text-gray-400">Aucune activité</p>
              ) : (
                <ul className="space-y-2">
                  {data.recentActivity.slice(0, 5).map((ev, i) => {
                    const Icon = EVENT_ICON[ev.eventType] || Activity;
                    const bg = EVENT_ICON_BG[ev.eventType] || 'bg-slate-100 text-slate-700';
                    const detail = ev.metadata?.path || ev.metadata?.name || ev.metadata?.query || '—';
                    return (
                      <li key={i} className="flex items-center gap-2" data-testid={`activity-row-${i}`}>
                        <div className={`w-8 h-8 rounded-full ${bg} flex items-center justify-center shrink-0`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-gray-900 leading-tight">{EVENT_LABEL[ev.eventType] || ev.eventType}</p>
                          <p className="text-[11px] text-gray-500 truncate">{detail}</p>
                        </div>
                        <span className="text-[10px] text-gray-400 whitespace-nowrap">{fmtRelative(ev.createdAt)}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <p className="text-xs text-gray-400 text-center pt-2">
        Les données d'engagement sont conservées 180 jours (TTL automatique sur <code>analytics_events</code>).
        Les emails individuels sont visibles uniquement pour les rôles admin/CFO.
      </p>

      {/* ════════ Modal "Voir toutes les fonctionnalités" — Roadmap d'adoption ════════ */}
      <Dialog open={showAllFeatures} onOpenChange={setShowAllFeatures}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="all-features-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-indigo-500" />
              Roadmap d'adoption produit
            </DialogTitle>
            <DialogDescription>
              Les 15 fonctionnalités critiques du portail client, regroupées par catégorie.
              Le statut est calculé sur la période sélectionnée ({days}j).
            </DialogDescription>
          </DialogHeader>

          <div className="overflow-auto flex-1 space-y-5 py-2">
            {FEATURE_CATEGORIES.filter(c => c.id !== 'all').map((cat) => {
              const rows = (data?.adoptionByFeature || []).filter(f => f.category === cat.id);
              if (rows.length === 0) return null;
              const critiques = rows.filter(r => r.status === 'critique').length;
              return (
                <div key={cat.id} data-testid={`feature-category-${cat.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                      <Badge className={`${CATEGORY_BADGE[cat.id]} border text-[10px] px-2 py-0.5`}>
                        {cat.label}
                      </Badge>
                      <span className="text-xs text-gray-500 font-normal">
                        {rows.length} fonctionnalité{rows.length > 1 ? 's' : ''}
                        {critiques > 0 && <span className="text-red-600 ml-2">• {critiques} critique{critiques > 1 ? 's' : ''}</span>}
                      </span>
                    </h3>
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gray-50">
                          <TableHead className="text-xs">Fonctionnalité</TableHead>
                          <TableHead className="text-xs text-right">Utilisations</TableHead>
                          <TableHead className="text-xs text-right">Sociétés</TableHead>
                          <TableHead className="text-xs">Impact</TableHead>
                          <TableHead className="text-xs">Action recommandée</TableHead>
                          <TableHead className="text-xs">Statut</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rows.map((f) => (
                          <TableRow key={f.feature} data-testid={`feature-row-${f.feature}`}>
                            <TableCell className="text-sm font-medium">
                              {f.label}
                              <code className="block text-[10px] text-gray-400 font-normal">{f.feature}</code>
                            </TableCell>
                            <TableCell className="text-right font-mono text-sm">{fmtNumber(f.uses)}</TableCell>
                            <TableCell className="text-right font-mono text-sm">{fmtNumber(f.companies)}</TableCell>
                            <TableCell><ImpactDots value={f.impact} /></TableCell>
                            <TableCell className="text-xs text-gray-700">{f.action}</TableCell>
                            <TableCell>
                              <Badge className={`${STATUS_BADGE[f.status]} border text-[10px] px-2 py-0.5`}>
                                {STATUS_LABEL[f.status]}
                              </Badge>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Modale Voir toutes les actions recommandées */}
      <AllRecommendedActionsModal
        open={showAllRecommended}
        onOpenChange={setShowAllRecommended}
        actions={data?.recommendedActions || []}
        clients={allClients}
      />
    </div>
  );
}

function KpiCard({ icon: Icon, iconBg, label, value, sub, subtitle, delta, testid }) {
  return (
    <Card data-testid={testid} className="overflow-hidden">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className={`w-8 h-8 rounded-lg ${iconBg} flex items-center justify-center`}>
            <Icon className="w-4 h-4" />
          </div>
        </div>
        <p className="text-[11px] text-gray-500 leading-tight">{label}</p>
        <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
        {delta !== null && delta !== undefined ? (
          <p className={`text-[11px] mt-1 flex items-center gap-1 ${delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-600' : 'text-gray-500'}`}>
            <TrendingUp className={`w-3 h-3 ${delta < 0 ? 'rotate-180' : ''}`} />
            {delta > 0 ? `+${delta}` : delta} vs période précédente
          </p>
        ) : subtitle ? (
          <p className="text-[11px] mt-1 text-gray-500">{subtitle}</p>
        ) : sub ? (
          <p className="text-[11px] mt-1 text-gray-400">{sub}</p>
        ) : null}
      </CardContent>
    </Card>
  );
}
